import { AlertCircle, Check, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import './WorkerSelector.css';

/**
 * WorkerSelector - Autocomplete component for worker selection
 *
 * @param {Object} props
 * @param {Array} props.workers - Array of worker objects
 * @param {string} props.value - Selected workerPaymentProfileId
 * @param {Function} props.onChange - Callback when selection changes
 * @param {string} props.label - Label for the field
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.required - Required field
 */
const WorkerSelector = ({
  workers = [],
  value,
  onChange,
  label = 'Percettore',
  placeholder = 'Cerca per nome, email o locale...',
  disabled = false,
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Get selected worker object
  const selectedWorker = useMemo(() => {
    return workers.find(w => w.workerPaymentProfileId === value);
  }, [workers, value]);

  // Filter workers based on search term
  const filteredWorkers = useMemo(() => {
    if (!searchTerm.trim()) return workers;

    const term = searchTerm.toLowerCase();

    return workers.filter(worker => {
      const fullName = `${worker.firstName} ${worker.lastName}`.toLowerCase();
      const email = (worker.email || '').toLowerCase();
      const venue = (worker.venueName || '').toLowerCase();
      const tenant = (worker.tenantName || '').toLowerCase();

      return fullName.includes(term) ||
        email.includes(term) ||
        venue.includes(term) ||
        tenant.includes(term);
    });
  }, [workers, searchTerm]);

  // Reset highlighted index when filtered list changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredWorkers]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Update dropdown position when opening or on scroll/resize
  useEffect(() => {
    const updatePosition = () => {
      if (containerRef.current && isOpen) {
        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const maxDropdownHeight = 500; // Increased from 400
        const padding = 8; // Reduced from 16 for more space

        // Calculate actual max height based on available space
        const calculatedMaxHeight = Math.min(
          maxDropdownHeight,
          spaceBelow - padding
        );

        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.max(200, calculatedMaxHeight) // Increased minimum from 150 to 200
        });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredWorkers.length - 1 ? prev + 1 : prev
        );
        break;

      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;

      case 'Enter':
        e.preventDefault();
        if (filteredWorkers[highlightedIndex]) {
          handleSelect(filteredWorkers[highlightedIndex]);
        }
        break;

      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;

      default:
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const highlighted = dropdownRef.current.querySelector('.worker-option.highlighted');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (worker) => {
    onChange(worker.workerPaymentProfileId);
    setIsOpen(false);
    setSearchTerm('');
    inputRef.current?.blur();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    inputRef.current?.focus();
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      inputRef.current?.focus();
    }
  };

  // Highlight matching text
  const highlightMatch = (text, term) => {
    if (!term.trim()) return text;

    const index = text.toLowerCase().indexOf(term.toLowerCase());
    if (index === -1) return text;

    return (
      <>
        {text.substring(0, index)}
        <mark className="highlight">{text.substring(index, index + term.length)}</mark>
        {text.substring(index + term.length)}
      </>
    );
  };

  // Get status indicator
  const getStatusInfo = (worker) => {
    if (!worker.hasPaymentProfile) return { icon: '✗', text: 'Nessun profilo pagamento', color: '#ef4444' };
    if (worker.canReceivePayments === false) return { icon: '⚠', text: 'Pagamenti non abilitati', color: '#f59e0b' };
    return { icon: '✓', text: 'Attivo', color: '#22c55e' };
  };

  return (
    <div className="worker-selector" ref={containerRef}>
      {label && (
        <label className="worker-selector-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}

      {/* Selected Worker Display */}
      {selectedWorker && !isOpen ? (
        <div
          className={`worker-selected-display ${disabled ? 'disabled' : ''}`}
          onClick={handleInputClick}
        >
          <div className="worker-selected-info">
            <div className="worker-selected-name">
              {selectedWorker.firstName} {selectedWorker.lastName}
            </div>
            <div className="worker-selected-meta">
              {selectedWorker.email} · {selectedWorker.venueName}
            </div>
          </div>
          {!disabled && (
            <button
              className="worker-clear-btn"
              onClick={handleClear}
              type="button"
              aria-label="Clear selection"
            >
              <X size={16} />
            </button>
          )}
        </div>
      ) : (
        /* Search Input */
        <div className="worker-search-container">
          <Search className="search-icon" size={18} />
          <input
            ref={inputRef}
            type="text"
            className="worker-search-input"
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => !disabled && setIsOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-label="Search workers"
            aria-autocomplete="list"
            aria-controls="worker-dropdown"
            aria-expanded={isOpen}
            role="combobox"
          />
          {searchTerm && (
            <button
              className="search-clear-btn"
              onClick={() => setSearchTerm('')}
              type="button"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          className="worker-dropdown"
          id="worker-dropdown"
          ref={dropdownRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxHeight: `${dropdownPosition.maxHeight || 400}px`
          }}
        >
          {filteredWorkers.length === 0 ? (
            <div className="worker-no-results">
              <AlertCircle size={20} />
              <span>Nessun percettore trovato</span>
            </div>
          ) : (
            <>
              <div className="worker-dropdown-header">
                {filteredWorkers.length} risultat{filteredWorkers.length === 1 ? 'o' : 'i'}
              </div>
              <div className="worker-options-list">
                {filteredWorkers.map((worker, index) => {
                  const status = getStatusInfo(worker);
                  const isHighlighted = index === highlightedIndex;
                  const isSelected = worker.workerPaymentProfileId === value;

                  return (
                    <div
                      key={worker.workerPaymentProfileId}
                      className={`worker-option ${isHighlighted ? 'highlighted' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelect(worker)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <div className="worker-option-content">
                        <div className="worker-option-name">
                          {highlightMatch(`${worker.firstName} ${worker.lastName}`, searchTerm)}
                          {isSelected && <Check size={12} className="selected-icon" />}
                        </div>
                        <div className="worker-option-sub">
                          {highlightMatch(worker.email, searchTerm)}
                          {worker.venueName ? <span className="worker-option-venue"> · {highlightMatch(worker.venueName, searchTerm)}</span> : null}
                        </div>
                      </div>
                      <div
                        className="worker-status-dot"
                        style={{ color: status.color }}
                        title={status.text}
                      >
                        {status.icon}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkerSelector;
